import { expandArcs } from './arcWalls.jsx';

// «فضاها» — هر بخشِ بسته‌ای که دیوارها دورش را گرفته‌اند، خودکار یک فضای مستقل است: کف، سقف، نور و پوششِ
// خودش را دارد. چه با «مدل‌های آماده» (اتاق‌خواب و …) ساخته شده باشد چه با کشیدنِ دیوار.
//
//   • اتاقِ داخلِ یک اتاقِ بزرگ‌تر، یک فضای جداست؛ کف/سقف/نورِ اتاقِ بزرگ دورِ آن را دور می‌زند (سوراخ دارد)
//   • بخشی که با دیوارهای دستی بسته شده هم سقف و کف می‌گیرد
//   • درزِ باریکِ دیوارِ بیرونی (تا ۱٫۷ متر) بسته حساب می‌شود — خانه‌ای که جای درش خالی مانده هم سقف دارد
//   • «مرزِ فضا» (دیوارِ virtual — بدونِ دیوارِ واقعی در سه‌بعدی) هم مثلِ دیوار فضاها را از هم جدا می‌کند
//
// روش: نقشه با خانه‌های ۵ سانتی شطرنجی می‌شود، دیوارها (بدونِ در/پنجره/بازشو — آن‌ها مرزِ فضا را باز نمی‌کنند)
// علامت می‌خورند، از بیرون «آب‌پاشی» (flood fill) می‌شود و هر تکه‌ی بسته‌ی باقی‌مانده یک فضاست. هر فضا به چند
// مستطیل (rects) تبدیل می‌شود تا کف و سقف با همان ابزارهای قبلی ساخته شوند.
//
// خروجیِ هر فضا (همان شکلِ قبلیِ roomGroupRects، به‌اضافه‌ی rects):
//   { id, label, kind: 'group' | 'free', x1, y1, x2, y2 (محدوده), rects: [{x1,y1,x2,y2}], area (cm²),
//     floorStyle, ceilingStyle, coverPrompt, wallIds, seed: {x, y} }
// به ترتیبِ مساحت از کوچک به بزرگ — هر جست‌وجوی «این نقطه در کدام فضاست» اول فضای داخلی‌تر را پیدا می‌کند

const MAX_CELLS = 1600000;
const MIN_AREA_CM2 = 5000; // کمتر از نیم متر مربع فضا حساب نمی‌شود (درزِ بینِ دیوارها)
const GAP_CLOSE_CM = 170; // درزِ دیوارِ بیرونی تا این پهنا «بسته» حساب می‌شود (جای درِ ورودی، دیوارِ ناقص)

// فاصله تا نزدیک‌ترین خانه‌ی src با معیارِ «مربعی» (قطری = ۱) — بر حسبِ خانه. مربعی، نه دایره‌ای: بستنِ درز با
// مربع، لبه‌ی صاف و دقیقاً روی خطِ دیوار می‌دهد (با دایره، جای درز یک فرورفتگیِ پله‌پله می‌ماند → کف و سقفِ تکه‌تکه)
function distanceTo(src, W, H) {
  const N = W * H;
  const d = new Float32Array(N);
  for (let i = 0; i < N; i += 1) d[i] = src[i] ? 0 : 1e9;
  const D = 1;
  for (let y = 0; y < H; y += 1) {
    for (let x = 0; x < W; x += 1) {
      const i = y * W + x;
      let v = d[i];
      if (x > 0) v = Math.min(v, d[i - 1] + 1);
      if (y > 0) {
        v = Math.min(v, d[i - W] + 1);
        if (x > 0) v = Math.min(v, d[i - W - 1] + D);
        if (x < W - 1) v = Math.min(v, d[i - W + 1] + D);
      }
      d[i] = v;
    }
  }
  for (let y = H - 1; y >= 0; y -= 1) {
    for (let x = W - 1; x >= 0; x -= 1) {
      const i = y * W + x;
      let v = d[i];
      if (x < W - 1) v = Math.min(v, d[i + 1] + 1);
      if (y < H - 1) {
        v = Math.min(v, d[i + W] + 1);
        if (x < W - 1) v = Math.min(v, d[i + W + 1] + D);
        if (x > 0) v = Math.min(v, d[i + W - 1] + D);
      }
      d[i] = v;
    }
  }
  return d;
}

// grid: ۱ دیوار، ۲ بیرون. خانه‌های «بیرونی» که در واقع پشتِ یک درزِ باریک‌اند → ۰ (= داخل، بعداً فضا می‌شوند)
function closeGaps(grid, W, H, r) {
  if (r < 1) return;
  const N = W * H;
  const wall = new Uint8Array(N);
  for (let i = 0; i < N; i += 1) wall[i] = grid[i] === 1 ? 1 : 0;
  const dw = distanceTo(wall, W, H);
  // بیرونِ واقعی: از لبه‌ها، فقط از خانه‌هایی که از هر دیوار دورتر از r هستند
  const out = new Uint8Array(N);
  const stack = new Int32Array(N);
  let sp = 0;
  const push = (i) => {
    if (!out[i] && dw[i] > r) {
      out[i] = 1;
      stack[sp++] = i;
    }
  };
  for (let x = 0; x < W; x += 1) {
    push(x);
    push((H - 1) * W + x);
  }
  for (let y = 0; y < H; y += 1) {
    push(y * W);
    push(y * W + W - 1);
  }
  while (sp > 0) {
    const i = stack[--sp];
    const x = i % W;
    if (x > 0) push(i - 1);
    if (x < W - 1) push(i + 1);
    if (i >= W) push(i - W);
    if (i < N - W) push(i + W);
  }
  const doo = distanceTo(out, W, H);
  for (let i = 0; i < N; i += 1) if (grid[i] === 2 && doo[i] > r + 0.5) grid[i] = 0;
}

// تکه‌ی ذوزنقه‌ای (کنارِ دیوار/مرزِ مورب): xl0/xr0 = چپ/راست در y1، xl1/xr1 = در y2 (x1/x2 = محدوده‌ی کل)
export const isTrapPiece = (q) => q.xl0 !== undefined && (q.xl0 !== q.xl1 || q.xr0 !== q.xr1);

export function pieceContains(q, x, z, pad = 0) {
  if (q.xl0 !== undefined) {
    if (z < q.y1 - pad || z > q.y2 + pad) return false;
    const t = q.y2 > q.y1 ? Math.max(0, Math.min(1, (z - q.y1) / (q.y2 - q.y1))) : 0;
    return x >= q.xl0 + (q.xl1 - q.xl0) * t - pad && x <= q.xr0 + (q.xr1 - q.xr0) * t + pad;
  }
  return (
    x >= Math.min(q.x1, q.x2) - pad &&
    x <= Math.max(q.x1, q.x2) + pad &&
    z >= Math.min(q.y1, q.y2) - pad &&
    z <= Math.max(q.y1, q.y2) + pad
  );
}

export function spaceContains(r, x, z, pad = 0) {
  const rects = r.rects && r.rects.length ? r.rects : [r];
  return rects.some((q) => pieceContains(q, x, z, pad));
}

export const pieceArea = (q) =>
  q.xl0 !== undefined ? ((q.xr0 - q.xl0 + (q.xr1 - q.xl1)) / 2) * (q.y2 - q.y1) : (q.x2 - q.x1) * (q.y2 - q.y1);

// بزرگ‌ترین مستطیلِ یک فضا (برای مرکز، دوربین، فیلم)
export function largestRect(r) {
  const rects = r.rects && r.rects.length ? r.rects : [r];
  return rects.reduce((best, q) =>
    Math.abs((q.x2 - q.x1) * (q.y2 - q.y1)) > Math.abs((best.x2 - best.x1) * (best.y2 - best.y1)) ? q : best
  );
}

export function spaceCenter(r) {
  const q = largestRect(r);
  return { x: (q.x1 + q.x2) / 2, y: (q.y1 + q.y2) / 2 };
}

// لبه‌هایی از تکه‌ی i که بیشترش به تکه‌ی دیگرِ همان فضا چسبیده (نه دیوار) → { z1, z2, x1, x2 }
export function innerPieceEdges(rects, i) {
  const p = rects[i];
  const shared = (axis, c, a, b, outward) =>
    rects.reduce((sum, q, j) => {
      if (j === i) return sum;
      if (axis === 'x') {
        const touches = outward < 0 ? Math.abs(q.y2 - c) < 0.5 : Math.abs(q.y1 - c) < 0.5;
        return touches ? sum + Math.max(0, Math.min(b, q.x2) - Math.max(a, q.x1)) : sum;
      }
      const touches = outward < 0 ? Math.abs(q.x2 - c) < 0.5 : Math.abs(q.x1 - c) < 0.5;
      return touches ? sum + Math.max(0, Math.min(b, q.y2) - Math.max(a, q.y1)) : sum;
    }, 0);
  const w = p.x2 - p.x1;
  const d = p.y2 - p.y1;
  return {
    z1: shared('x', p.y1, p.x1, p.x2, -1) > w / 2,
    z2: shared('x', p.y2, p.x1, p.x2, 1) > w / 2,
    x1: shared('z', p.x1, p.y1, p.y2, -1) > d / 2,
    x2: shared('z', p.x2, p.y1, p.y2, 1) > d / 2,
  };
}

function segDist2(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? ((px - x1) * dx + (py - y1) * dy) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = x1 + dx * t - px;
  const cy = y1 + dy * t - py;
  return cx * cx + cy * cy;
}

// walls: دیوارهای پلان. groups: [{ id, label, bounds: {x1,y1,x2,y2}, wallIds, floorStyle, ceilingStyle, coverPrompt }]
// freeSpaces: [{ id, seed: {x, y}, label?, floorStyle?, ceilingStyle?, coverPrompt? }] (تنظیماتِ ذخیره‌شده‌ی فضاهای دستی)
// namedRooms: اتاق‌هایی که با ابزارِ «اتاق» نام گرفته‌اند [{ centroid: {x, y}, name }]
// opts.footprint: به‌جای فضاها، «زیرِ کلِ خانه» (همه‌ی داخل + زیرِ دیوارها، با درزهای باریکِ بسته) به‌صورتِ تکه‌های
// مستطیل/ذوزنقه — زیرسازیِ ساده‌ی کف و سقف تا هیچ جای خالیِ کوچکی (گوشه‌ی تیز، تکه‌ی ریزِ بینِ دو مرز) باز نماند
export function computeSpaces(walls, groups = [], freeSpaces = [], namedRooms = [], opts = null) {
  // دیوار/مرزِ منحنی → تکه‌های صافِ کوتاه (arcWalls.jsx)
  const valid = (expandArcs(walls) || []).filter((w) => Math.hypot(w.x2 - w.x1, w.y2 - w.y1) > 1);
  if (!valid.length) return groupFallback(groups);

  let C = 5;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  valid.forEach((w) => {
    minX = Math.min(minX, w.x1, w.x2);
    minY = Math.min(minY, w.y1, w.y2);
    maxX = Math.max(maxX, w.x1, w.x2);
    maxY = Math.max(maxY, w.y1, w.y2);
  });
  while (((maxX - minX) / C + 8) * ((maxY - minY) / C + 8) > MAX_CELLS) C *= 2;
  // حاشیه‌ی بیرونی باید از نصفِ درز پهن‌تر باشد تا «بیرون» از لبه‌ها پیدا شود (closeGaps)
  const PAD = 3 + Math.round(GAP_CLOSE_CM / 2 / C) + 2;
  const ox = Math.floor(minX / C) * C - PAD * C;
  const oy = Math.floor(minY / C) * C - PAD * C;
  const W = Math.ceil((maxX - ox) / C) + PAD + 1;
  const H = Math.ceil((maxY - oy) / C) + PAD + 1;
  const N = W * H;

  // ۰ = خالی، ۱ = دیوار، ۲ = بیرون، ۳.. = شماره‌ی فضا
  const grid = new Int32Array(N);
  const owner = new Int32Array(N).fill(-1); // کدام دیوار (اندیس در valid)
  const band = C * 0.76;
  const band2 = band * band;
  valid.forEach((w, wi) => {
    const gx0 = Math.max(0, Math.floor((Math.min(w.x1, w.x2) - band - ox) / C));
    const gx1 = Math.min(W - 1, Math.ceil((Math.max(w.x1, w.x2) + band - ox) / C));
    const gy0 = Math.max(0, Math.floor((Math.min(w.y1, w.y2) - band - oy) / C));
    const gy1 = Math.min(H - 1, Math.ceil((Math.max(w.y1, w.y2) + band - oy) / C));
    for (let gy = gy0; gy <= gy1; gy += 1) {
      const py = oy + (gy + 0.5) * C;
      for (let gx = gx0; gx <= gx1; gx += 1) {
        const px = ox + (gx + 0.5) * C;
        if (segDist2(px, py, w.x1, w.y1, w.x2, w.y2) <= band2) {
          const i = gy * W + gx;
          grid[i] = 1;
          if (owner[i] < 0) owner[i] = wi;
        }
      }
    }
  });

  // بیرون: از لبه‌ها
  const stack = new Int32Array(N);
  let sp = 0;
  const pushIf = (i) => {
    if (grid[i] === 0) {
      grid[i] = 2;
      stack[sp++] = i;
    }
  };
  for (let x = 0; x < W; x += 1) {
    pushIf(x);
    pushIf((H - 1) * W + x);
  }
  for (let y = 0; y < H; y += 1) {
    pushIf(y * W);
    pushIf(y * W + W - 1);
  }
  while (sp > 0) {
    const i = stack[--sp];
    const x = i % W;
    if (x > 0) pushIf(i - 1);
    if (x < W - 1) pushIf(i + 1);
    if (i >= W) pushIf(i - W);
    if (i < N - W) pushIf(i + W);
  }

  // درزهای دیوارِ بیرونی (مثلاً جای در که خالی مانده، یا دیوارهایی که کاملاً به هم نرسیده‌اند): بخشی که از درزی کمتر
  // از GAP_CLOSE_CM به بیرون راه دارد هم داخلِ خانه است و کف و سقف می‌گیرد («بستنِ» ریخت‌شناسی: دیوارها به اندازه‌ی
  // نصفِ درز پهن، آب‌پاشی از بیرون، و دوباره همان اندازه برگشت). حیاط و تورفتگی‌های پهن بیرون می‌مانند
  closeGaps(grid, W, H, Math.round(GAP_CLOSE_CM / 2 / C));

  if (opts && opts.footprint) {
    const rows = [];
    for (let y = 0; y < H; y += 1) {
      let x = 0;
      while (x < W) {
        if (grid[y * W + x] === 2) {
          x += 1;
          continue;
        }
        const a = x;
        while (x < W && grid[y * W + x] !== 2) x += 1;
        rows.push({ x1: ox + a * C, x2: ox + x * C, y1: oy + y * C, y2: oy + (y + 1) * C });
      }
    }
    const ax = [...new Set(valid.flatMap((w) => [w.x1, w.x2]))].sort((a, b) => a - b);
    const ay = [...new Set(valid.flatMap((w) => [w.y1, w.y2]))].sort((a, b) => a - b);
    const dg = valid.filter((w) => Math.abs(w.x2 - w.x1) > 1 && Math.abs(w.y2 - w.y1) > 1);
    return fitDiagonals(cleanRects(rows, ax, ay, C * 1.7), dg, C * 3);
  }

  // تکه‌های بسته
  const regions = [];
  let label = 3;
  for (let s = 0; s < N; s += 1) {
    if (grid[s] !== 0) continue;
    const cells = [];
    const wallSet = new Set();
    grid[s] = label;
    stack[0] = s;
    sp = 1;
    while (sp > 0) {
      const i = stack[--sp];
      cells.push(i);
      const x = i % W;
      const nbs = [x > 0 ? i - 1 : -1, x < W - 1 ? i + 1 : -1, i >= W ? i - W : -1, i < N - W ? i + W : -1];
      for (const j of nbs) {
        if (j < 0) continue;
        if (grid[j] === 0) {
          grid[j] = label;
          stack[sp++] = j;
        } else if (grid[j] === 1 && owner[j] >= 0) wallSet.add(owner[j]);
      }
    }
    if (cells.length * C * C >= MIN_AREA_CM2) regions.push({ label, cells, wallIds: [...wallSet].map((wi) => valid[wi].id) });
    label += 1;
  }

  // کف و سقف تا محورِ دیوار ادامه پیدا می‌کنند (همان قراردادِ قبلی): هر فضا یک ردیف خانه از دیوارهای کنارش را
  // هم برمی‌دارد — دیوار دو خانه پهنا دارد، پس دو فضای دو طرفِ یک دیوار دقیقاً روی محورِ آن به هم می‌رسند
  const taken = new Uint8Array(N);
  regions.forEach((reg) => {
    const extra = [];
    reg.cells.forEach((i) => {
      const x = i % W;
      // ۸ همسایه، تا خانه‌ی گوشه‌ها هم برداشته شود (وگرنه کنارِ هر گوشه یک شکافِ ۵ سانتی می‌ماند)
      const y = (i - x) / W;
      const nbs = [];
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          if ((dx || dy) && x + dx >= 0 && x + dx < W && y + dy >= 0 && y + dy < H) nbs.push(i + dy * W + dx);
        }
      }
      nbs.forEach((j) => {
        if (grid[j] === 1 && !taken[j]) {
          taken[j] = 1;
          extra.push(j);
        }
      });
    });
    reg.cells = reg.cells.concat(extra);
  });

  // خطوطِ محورِ دیوارها — لبه‌ی کف/سقف دقیقاً روی همین‌ها می‌نشیند (cleanRects)
  const axisX = [...new Set(valid.flatMap((w) => [w.x1, w.x2]))].sort((a, b) => a - b);
  const axisY = [...new Set(valid.flatMap((w) => [w.y1, w.y2]))].sort((a, b) => a - b);
  // دیوارها/مرزهای مورب — لبه‌ی کف/سقف رویشان صاف و مورب می‌شود، نه پله‌پله (fitDiagonals)
  const diagWalls = valid.filter((w) => Math.abs(w.x2 - w.x1) > 1 && Math.abs(w.y2 - w.y1) > 1);

  // هر فضا → مستطیل‌ها (ردیف‌های یکسانِ پشتِ‌سرِهم ادغام می‌شوند)
  const out = regions.map((reg) => {
    const rows = new Map();
    reg.cells.forEach((i) => {
      const y = Math.floor(i / W);
      if (!rows.has(y)) rows.set(y, []);
      rows.get(y).push(i % W);
    });
    const ys = [...rows.keys()].sort((a, b) => a - b);
    const open = new Map(); // "x0,x1" → rect (در خانه‌ها)
    const rectsCells = [];
    let prevY = null;
    ys.forEach((y) => {
      const xs = rows.get(y).sort((a, b) => a - b);
      const runs = [];
      let a = xs[0];
      let b = xs[0];
      for (let k = 1; k < xs.length; k += 1) {
        if (xs[k] === b + 1) b = xs[k];
        else {
          runs.push([a, b]);
          a = xs[k];
          b = xs[k];
        }
      }
      runs.push([a, b]);
      const keys = new Set(runs.map((r) => `${r[0]},${r[1]}`));
      // مستطیل‌هایی که ادامه پیدا نکردند بسته می‌شوند
      [...open.keys()].forEach((k) => {
        if (!keys.has(k) || prevY !== y - 1) {
          rectsCells.push(open.get(k));
          open.delete(k);
        }
      });
      runs.forEach(([x0, x1]) => {
        const k = `${x0},${x1}`;
        if (open.has(k)) open.get(k).y1 = y;
        else open.set(k, { x0, x1, y0: y, y1: y });
      });
      prevY = y;
    });
    open.forEach((r) => rectsCells.push(r));

    const rects = fitDiagonals(
      cleanRects(
        rectsCells.map((r) => ({
          x1: ox + r.x0 * C,
          x2: ox + (r.x1 + 1) * C,
          y1: oy + r.y0 * C,
          y2: oy + (r.y1 + 1) * C,
        })),
        axisX,
        axisY,
        C * 1.7
      ),
      diagWalls,
      C * 3
    );
    if (!rects.length) return null;
    const bx1 = Math.min(...rects.map((q) => q.x1));
    const by1 = Math.min(...rects.map((q) => q.y1));
    const bx2 = Math.max(...rects.map((q) => q.x2));
    const by2 = Math.max(...rects.map((q) => q.y2));
    const area = rects.reduce((a, q) => a + pieceArea(q), 0);
    // نقطه‌ی «نشانِ» فضا (تنظیماتِ کف/سقفِ ذخیره‌شده با همین پیدا می‌شوند) — حتماً داخلِ فضا: وسطِ بزرگ‌ترین تکه در
    // ارتفاعِ میانی‌اش (برای ذوزنقه/مثلث وسطِ کادر ممکن بود بیرونِ فضا بیفتد و تنظیمات اعمال نمی‌شد)
    const big = rects.reduce((best, q) => (pieceArea(q) > pieceArea(best) ? q : best));
    const midX =
      big.xl0 !== undefined ? (big.xl0 + big.xl1 + big.xr0 + big.xr1) / 4 : (big.x1 + big.x2) / 2;
    const seed = { x: Math.round(midX), y: Math.round((big.y1 + big.y2) / 2) };
    return { rects, x1: bx1, y1: by1, x2: bx2, y2: by2, area, seed, wallIds: reg.wallIds };
  }).filter(Boolean);

  // صاحبِ هر «اتاقِ آماده»: تکه‌ای که بیشترین سطح را داخلِ محدوده‌ی آن اتاق دارد (فقط یک تکه). تکه‌های دیگرِ داخلِ
  // همان محدوده — مثلاً اتاقِ کوچکی که داخلش کشیده شده — فضای مستقلِ خودشان هستند
  const claimed = new Set();
  const regionGroup = new Map(); // اندیسِ تکه → گروه
  [...groups]
    .filter((g) => g.bounds)
    .sort((a, b) => areaOf(a.bounds) - areaOf(b.bounds))
    .forEach((g) => {
      let best = -1;
      let bestOv = 0;
      out.forEach((sp0, k) => {
        if (regionGroup.has(k)) return;
        const ov = overlap(sp0.rects, g.bounds);
        if (ov > bestOv) {
          bestOv = ov;
          best = k;
        }
      });
      if (best >= 0 && bestOv > out[best].area * 0.5) {
        regionGroup.set(best, g);
        claimed.add(g.id);
      }
    });
  const result = out.map((sp0, k) => {
    const inGroup = regionGroup.get(k);
    if (inGroup) {
      // اتاقِ آماده‌ی ساده (بدونِ اتاقِ داخلی): همان مستطیلِ دقیقِ خودش
      const single = !inGroup.curved && sp0.rects.length === 1 && sp0.area > areaOf(inGroup.bounds) * 0.85;
      return {
        ...sp0,
        id: inGroup.id,
        kind: 'group',
        label: inGroup.label,
        ...(single ? { x1: inGroup.bounds.x1, y1: inGroup.bounds.y1, x2: inGroup.bounds.x2, y2: inGroup.bounds.y2 } : {}),
        rects: single ? [{ ...inGroup.bounds }] : sp0.rects,
        floorStyle: inGroup.floorStyle,
        ceilingStyle: inGroup.ceilingStyle,
        coverPrompt: inGroup.coverPrompt,
        wallIds: inGroup.wallIds,
      };
    }
    const stored = freeSpaces.find((f) => f.seed && spaceContains(sp0, f.seed.x, f.seed.y, 1));
    const named = namedRooms.find((r) => r.centroid && spaceContains(sp0, r.centroid.x, r.centroid.y));
    return {
      ...sp0,
      id: stored ? stored.id : `auto-${sp0.seed.x}-${sp0.seed.y}`,
      kind: 'free',
      label: (stored && stored.label) || (named && named.name) || 'فضا',
      floorStyle: stored && stored.floorStyle,
      ceilingStyle: stored && stored.ceilingStyle,
      coverPrompt: stored && stored.coverPrompt,
      seed: stored && stored.seed ? stored.seed : sp0.seed,
    };
  });
  // اتاقِ آماده‌ای که تکه‌ی بسته‌ای پیدا نکرد (مثلاً دیوارهایش هنوز کامل نیست) — مثلِ قبل، همان مستطیل
  groups.forEach((g) => {
    if (!claimed.has(g.id) && g.bounds) result.push(groupSpace(g));
  });
  // یک فضای ذخیره‌شده ممکن است به دو تکه برسد — شناسه باید یکتا بماند
  const seen = new Set();
  result.forEach((r) => {
    if (seen.has(r.id)) r.id = `${r.id}~${r.seed.x}-${r.seed.y}`;
    seen.add(r.id);
  });
  return result.sort((a, b) => a.area - b.area);
}

// لبه‌های پله‌پله‌ی شطرنجی → روی نزدیک‌ترین خطِ محورِ دیوار (تا tol)، بعد اجتماعِ مستطیل‌ها دوباره به کمترین تعداد
// مستطیل شکسته می‌شود: اتاقِ مستطیلی = ۱ تکه، L = ۲ تکه (هر تکه کف/سقفِ جدا دارد؛ تکه‌ی اضافه = درز + سنگینی)
function cleanRects(rects, axisX, axisY, tol) {
  const snap = (v, axis) => {
    let best = v;
    let bd = tol;
    for (let i = 0; i < axis.length; i += 1) {
      const d = Math.abs(axis[i] - v);
      if (d <= bd) {
        bd = d;
        best = axis[i];
      }
    }
    return best;
  };
  const snapped = rects
    .map((q) => ({ x1: snap(q.x1, axisX), x2: snap(q.x2, axisX), y1: snap(q.y1, axisY), y2: snap(q.y2, axisY) }))
    .filter((q) => q.x2 - q.x1 > 0.5 && q.y2 - q.y1 > 0.5);
  if (snapped.length <= 1) return snapped;
  const xs = [...new Set(snapped.flatMap((q) => [q.x1, q.x2]))].sort((a, b) => a - b);
  const ys = [...new Set(snapped.flatMap((q) => [q.y1, q.y2]))].sort((a, b) => a - b);
  const nx = xs.length - 1;
  const ny = ys.length - 1;
  const ix = new Map(xs.map((v, i) => [v, i]));
  const iy = new Map(ys.map((v, i) => [v, i]));
  const cov = new Uint8Array(nx * ny);
  snapped.forEach((q) => {
    for (let j = iy.get(q.y1); j < iy.get(q.y2); j += 1) for (let i = ix.get(q.x1); i < ix.get(q.x2); i += 1) cov[j * nx + i] = 1;
  });
  // ردیف به ردیف: تکه‌های پیوسته؛ ردیفِ بعد با همان تکه‌ها → همان مستطیل بلندتر می‌شود
  const out = [];
  let open = new Map();
  for (let j = 0; j < ny; j += 1) {
    const runs = [];
    let i = 0;
    while (i < nx) {
      if (!cov[j * nx + i]) {
        i += 1;
        continue;
      }
      const a = i;
      while (i < nx && cov[j * nx + i]) i += 1;
      runs.push(`${a},${i}`);
    }
    const next = new Map();
    runs.forEach((k) => {
      if (open.has(k)) {
        const r = open.get(k);
        r.y2 = ys[j + 1];
        next.set(k, r);
        open.delete(k);
      } else {
        const [a, b] = k.split(',').map(Number);
        next.set(k, { x1: xs[a], x2: xs[b], y1: ys[j], y2: ys[j + 1] });
      }
    });
    open.forEach((r) => out.push(r));
    open = next;
  }
  open.forEach((r) => out.push(r));
  return out;
}

// تکه‌های پله‌پله‌ی کنارِ یک دیوارِ مورب → ذوزنقه‌هایی که لبه‌شان دقیقاً روی خطِ همان دیوار است، و تکه‌های پشتِ‌سرِهمِ
// کنارِ همان دیوار یکی می‌شوند (یک مثلث/ذوزنقه به‌جای ده‌ها نوارِ ۵ سانتی)
function fitDiagonals(rects, diags, tol) {
  if (!diags.length || !rects.length) return rects;
  const wx = (w, y) => w.x1 + ((w.x2 - w.x1) * (y - w.y1)) / (w.y2 - w.y1);
  const fit = (x, y1, y2) => {
    let best = null;
    diags.forEach((w, k) => {
      const lo = Math.min(w.y1, w.y2);
      const hi = Math.max(w.y1, w.y2);
      if (y1 < lo - 1 || y2 > hi + 1) return;
      const a = wx(w, y1);
      const b = wx(w, y2);
      const d = x < Math.min(a, b) ? Math.min(a, b) - x : x > Math.max(a, b) ? x - Math.max(a, b) : 0;
      if (d <= tol && (!best || d < best.d)) best = { k, a, b, d };
    });
    return best;
  };
  // سرِ تکه‌های مورب (مثلاً رأس‌های یک قوس) وسطِ یک ردیف نیفتد: ردیف همان‌جا دو نیم می‌شود تا هر نیمه کنارِ یک تکه‌ی صاف باشد
  const vys = [...new Set(diags.flatMap((w) => [w.y1, w.y2]))].sort((p1, p2) => p1 - p2);
  const split = [];
  rects.forEach((q) => {
    let y0 = q.y1;
    vys.forEach((vy) => {
      if (vy > y0 + 0.01 && vy < q.y2 - 0.01) {
        split.push({ ...q, y1: y0, y2: vy });
        y0 = vy;
      }
    });
    split.push({ ...q, y1: y0 });
  });
  const pieces = split.map((q) => {
    const L = fit(q.x1, q.y1, q.y2);
    const Rt = fit(q.x2, q.y1, q.y2);
    const p = {
      y1: q.y1,
      y2: q.y2,
      xl0: L ? L.a : q.x1,
      xl1: L ? L.b : q.x1,
      xr0: Rt ? Rt.a : q.x2,
      xr1: Rt ? Rt.b : q.x2,
      kl: L ? `w${L.k}` : `c${q.x1}`,
      kr: Rt ? `w${Rt.k}` : `c${q.x2}`,
    };
    // سرِ مثلث: چپ و راست از هم رد نشوند
    if (p.xl0 > p.xr0) p.xl0 = p.xr0 = (p.xl0 + p.xr0) / 2;
    if (p.xl1 > p.xr1) p.xl1 = p.xr1 = (p.xl1 + p.xr1) / 2;
    return p;
  });
  if (!pieces.some((p) => p.kl[0] === 'w' || p.kr[0] === 'w')) return rects;
  // ادغامِ عمودی: همان دیوارِ چپ و راست، پشتِ‌سرِهم
  pieces.sort((a, b) => a.y1 - b.y1 || a.xl0 - b.xl0);
  const out = [];
  pieces.forEach((p) => {
    const prev = out.find(
      (o) =>
        o.kl === p.kl &&
        o.kr === p.kr &&
        Math.abs(o.y2 - p.y1) < 0.01 &&
        Math.abs(o.xl1 - p.xl0) < 0.6 &&
        Math.abs(o.xr1 - p.xr0) < 0.6
    );
    if (prev) {
      prev.y2 = p.y2;
      prev.xl1 = p.xl1;
      prev.xr1 = p.xr1;
    } else out.push({ ...p });
  });
  return out
    .filter((p) => p.y2 - p.y1 > 0.5 && Math.max(p.xr0 - p.xl0, p.xr1 - p.xl1) > 0.5)
    .map((p) => {
      const trap = p.xl0 !== p.xl1 || p.xr0 !== p.xr1;
      const base = { x1: Math.min(p.xl0, p.xl1), x2: Math.max(p.xr0, p.xr1), y1: p.y1, y2: p.y2 };
      return trap ? { ...base, xl0: p.xl0, xl1: p.xl1, xr0: p.xr0, xr1: p.xr1 } : base;
    });
}

function overlap(rects, b) {
  const bx1 = Math.min(b.x1, b.x2);
  const bx2 = Math.max(b.x1, b.x2);
  const by1 = Math.min(b.y1, b.y2);
  const by2 = Math.max(b.y1, b.y2);
  return rects.reduce(
    (s, q) => s + Math.max(0, Math.min(q.x2, bx2) - Math.max(q.x1, bx1)) * Math.max(0, Math.min(q.y2, by2) - Math.max(q.y1, by1)),
    0
  );
}

function areaOf(b) {
  return Math.abs((b.x2 - b.x1) * (b.y2 - b.y1));
}

function groupSpace(g) {
  const b = g.bounds;
  return {
    id: g.id,
    kind: 'group',
    label: g.label,
    x1: b.x1,
    y1: b.y1,
    x2: b.x2,
    y2: b.y2,
    rects: [{ ...b }],
    area: areaOf(b),
    seed: { x: (b.x1 + b.x2) / 2, y: (b.y1 + b.y2) / 2 },
    floorStyle: g.floorStyle,
    ceilingStyle: g.ceilingStyle,
    coverPrompt: g.coverPrompt,
    wallIds: g.wallIds,
  };
}

function groupFallback(groups) {
  return groups.filter((g) => g.bounds).map(groupSpace).sort((a, b) => a.area - b.area);
}
